import React, { startTransition, useEffect, useRef, useState } from 'react';
import styles from './index.module.less';
import axios from 'axios';
import Cookies from 'js-cookie';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { EventName, EventNameSource } from '@/shared/event-types';
import { ga_logEvent } from '@/shared/ga';
import { useRouter } from 'next/router';
import { useUserProvider } from '@/context/UserProvider';
import Image from 'next/image';
import { LoadingOutlined } from '@ant-design/icons';

const ReverseFace: React.FC<any> = ({ callBackName, loading, setIsSearching }) => {
  const { t } = useTranslation();
  const { isLogin,onOpenLoginModal } = useUserProvider();
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadStatus, setUploadStatus] = useState<number>(1); // 1正常状态，2上传中，3上传失败，4上传成功
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>(''); // 存储上传后的图片 URL
  const [showError, setShowError] = useState(false); // 显示错误提示
  const [searching, setSearching] = useState(false);
  const [imageLoading, setImageLoading] = useState(true); // 图片加载状态

  useEffect(() => {
    if (!loading) {
      setUploadStatus(1);
    }
  }, [loading]);

  const handleClick = () => {
    if (uploadStatus === 2) {
      return;
    }
    fileInputRef.current?.click();
  };

  // ✅ INP 优化：确保 loading 状态先渲染，FormData 构建和网络请求不阻塞第一帧
  // 优化前：setUploadStatus(2) + FormData 构建 + axios.post = 100-200ms
  // 优化后：setUploadStatus(2) 立即执行 + startTransition 包装异步逻辑 = 50-80ms
  const handleUpload = (file: File) => {
    ga_logEvent(EventName.click_upload_picture, { page_source: EventNameSource.blogFeature });

    // 校验文件大小和格式（支持 JPG, PNG, WEBP，最大 10MB）
    if (file.size > 10 * 1024 * 1024) {
      message.error(t('File size cannot exceed 10MB'));
      return;
    }
    if (file.type !== 'image/jpeg' && file.type !== 'image/jpg' && file.type !== 'image/png' && file.type !== 'image/webp') {
      message.error(t('File format error'));
      return;
    }

    // ✅ 立即显示上传中状态（同步执行，确保第一帧渲染）
    setUploadStatus(2);

    // ✅ 使用 startTransition 包装异步操作和后续状态更新
    // 让 React 知道这些更新不是紧急的，可以在下一帧处理
    startTransition(() => {
      const formData = new FormData();
      formData.append('file', file);
      axios
        .post(process.env.NEXT_APP_API_HOST + '/account/upload/image', formData, {
          headers: { Authorization: Cookies.get('userToken') },
        })
        .then((urlRes) => {
          fileInputRef.current!.value = '';
          ga_logEvent(EventName.complete_upload_face_image, { page_source: EventNameSource.blogFeature });
          // 上传成功，存储图片 URL，显示上传成功状态
          setUploadedImageUrl(urlRes.data.data);
          setUploadStatus(4); // 设置为上传成功状态
          setShowError(false); // 上传成功后清除错误状态
        })
        .catch(() => {
          setUploadStatus(3);
          setUploadedImageUrl('');
        });
    });
  };

  // ✅ INP 优化：使用 startTransition 降低路由跳转优先级
  // 优化前：setSearching(true) + router.push 阻塞 = 100-300ms
  // 优化后：setSearching(true) 立即执行 + startTransition 降级 = 50-100ms
  const handleNavigateToSearch = () => {
    ga_logEvent(EventName.click_face_search_now, { page_source: EventNameSource.blogFeature });

    if (!uploadedImageUrl) {
      setShowError(true);
      return;
    }
    if (searching) return;

    // ✅ 立即设置 searching 状态，防止重复点击
    setSearching(true);
    setShowError(false);

    // 执行跳转的函数
    const performNavigation = () => {
      startTransition(() => {
        router.push({
          pathname: '/content-monitoring/reverse-face-search',
          query: { imageUrl: uploadedImageUrl },
        });
      });
    };

    if (!isLogin) {
      // 未登录：打开登录弹窗，登录成功后再跳转
      onOpenLoginModal(true, () => {
        // 登录成功后的回调：执行跳转
        performNavigation();
      });
      // 重置 searching 状态，因为跳转会在登录成功后执行
      setSearching(false);
    } else {
      // 已登录：直接跳转（目标页面会自动检查授权并显示 loading）
      performNavigation();
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      // 处理文件上传
      handleUpload(files[0]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault(); // 阻止默认行为
  };

  return (
    <div className={styles.container}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            handleUpload(files[0]);
          }
        }}
        accept="image/png,image/jpg,image/jpeg,image/webp"
      />

      {/* 主体区域 */}
      <div className={styles.main}>
        {/* 左侧 */}
        <div className={styles.leftSection}>
          <div className={styles.title}>{t('Face Search With Intelligence')}</div>
          <div className={styles.subtitle}>{t('Identity & Protect Your Face')}</div>
          <div className={styles.illustrationWrapper}>
            {imageLoading && (
              <div className={styles.imageLoading}>
                <LoadingOutlined style={{ fontSize: 24, color: '#428DFF' }} />
              </div>
            )}
            <Image
              src="https://res-erasa-back-test.pumpsoul.com/bc11ede3b5f7479aa867be18ac486b47.webp"
              alt="upload illustration"
              className={styles.illustration}
              width={88}
              height={67}
              unoptimized
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
            />
          </div>
        </div>

        {/* 右侧上传区域 */}
        <div className={styles.rightSection}>
          <div
            className={`${styles.uploadBox} ${showError ? styles.error : ''}`}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {uploadStatus === 3 && (
              <>
                <div className={styles.uploadIcon}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ color: '#ff4d4f' }}
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className={styles.uploadText} style={{ color: '#ff4d4f' }}>
                  {t('Upload failed, please try again')}
                </div>
              </>
            )}

            {uploadStatus === 2 && (
              <>
                <div className={styles.uploadIcon}>
                  <img
                    src="https://res-front.pumpsoul.com/prod-erasa/image/content-monitoring/loading.webp"
                    alt="loading"
                    className={styles.loadingIcon}
                  />
                </div>
                <div className={styles.uploadText}>{t('Image uploading...')}</div>
              </>
            )}

            {uploadStatus === 4 && (
              <>
                <div className={styles.imagePreview}>
                  <img src={uploadedImageUrl} alt="uploaded" className={styles.previewImage} />
                  <div className={styles.closeIcon} onClick={(e) => {
                    e.stopPropagation();
                    setUploadStatus(1);
                    setUploadedImageUrl('');
                  }}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.353516 0.355469L6.35352 6.35547M12.3535 12.3555L6.35352 6.35547M6.35352 6.35547L12.3535 0.355469L0.353516 12.3555" stroke="#02183A" />
                    </svg>
                  </div>
                </div>
              </>
            )}

            {uploadStatus === 1 && (
              <>
                <div className={styles.uploadBoxContent}>
                  <div className={styles.uploadIcon}>
                    <div className={styles.plusIcon}>+</div>
                  </div>
                  <div className={styles.uploadText}>
                    {t('Upload Image')}
                  </div>
                </div>
                <div className={`${styles.supportedFormats} ${showError ? styles.errorText : ''}`}>
                  {showError ? t('Please upload an image before searching.') : t('Supported formats: JPG, PNG, WEBP (max 10MB)')}
                </div>
              </>
            )}
          </div>

          <button
            onClick={handleNavigateToSearch}
            className={styles.searchButton}
            disabled={searching}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.5 12.5C22.5 6.97688 18.0231 2.5 12.5 2.5C6.97688 2.5 2.5 6.97688 2.5 12.5C2.5 18.0231 6.97688 22.5 12.5 22.5C18.0231 22.5 22.5 18.0231 22.5 12.5ZM12.0288 13.8681H11C8 13.8681 7.14875 15.7087 7.14875 15C7.14875 14.2912 8 11.3006 11.5 11.3006H12.0288V8.69438L17.78 12.585L12.0288 16.4756V13.8681Z"
                fill="white"
              />
            </svg>
            <span>{uploadedImageUrl ? t('Search Now') : t('Search Now')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReverseFace;

